import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { take } from 'rxjs/operators';
import { FlyoutLangSelectConfirmation } from '../../models/flyout-lang-select-confirmation';
import { LanguageDetails } from '../../models/utils';
import { LanguageRedirectionHandlerService } from '../../services/language-redirection';
import { CountryLanguageHelper } from '../../utils/country-language/country-language-helper';
import { FlyoutLangSelectorFacade } from '../flyout/flyout-lang-selector/flyout-lang-selector.facade';

@Injectable({
  providedIn: 'root'
})
export class LanguageRedirectionFacadeService {
  transSitesLangService = inject(LanguageRedirectionHandlerService);
  selectorFacade = inject(FlyoutLangSelectorFacade);
  countryLanguageHelper = inject(CountryLanguageHelper);

  flyOutConfirmationContent$: Observable<FlyoutLangSelectConfirmation> | null =
    null;

  countryCode = '';
  languageCode = '';
  secondaryLanguageCode = '';
  showLangSelectionModal = true;

  setPrefCookieOnSelect(isPrimarySelected: boolean): void {
    this.transSitesLangService.openFlyoutLang
      .pipe(take(1))
      .subscribe((openFlyoutLangSub) => {
        if (openFlyoutLangSub?.openFlyoutConfirmationModal) {
          this.countryCode = openFlyoutLangSub.countryCode;
          this.languageCode = openFlyoutLangSub.languageCode;
          this.secondaryLanguageCode = openFlyoutLangSub.secondaryLanguageCode;
          this.processLanguageSelection(isPrimarySelected);
        }
      });
  }

  private processLanguageSelection(isPrimarySelected: boolean): void {
    this.countryLanguageHelper.setShowConfirmationModal(false);
    const selectedLanguage = isPrimarySelected
      ? this.languageCode
      : this.secondaryLanguageCode;
    this.setPrefCookieOnLangSelect(this.countryCode, selectedLanguage);
    if (!isPrimarySelected) {
      this.countryLanguageHelper.redirectToTranslatedSites(
        this.countryCode,
        this.secondaryLanguageCode
      );
    }
  }

  setPrefCookieOnLangSelect(countryCode: string, languageCode: string): void {
    const prefValue = `${languageCode}-${countryCode}`;
    this.countryLanguageHelper.setPrefCookie(prefValue);
    this.countryLanguageHelper.setPreferredLanguages(prefValue);
  }

  openCLS(): void {
    this.selectorFacade.showSelectorModal.set(true);
    this.selectorFacade.init();
    this.showLangSelectionModal = false;
  }

  close(): void {
    this.showLangSelectionModal = false;
    this.selectorFacade.showSelectorModal.set(true);
  }

  setCLSData(
    countryCode: string,
    languageCode: string,
    secondaryLanguageCode: string,
    flyoutLangSelectorLink: string
  ): void {
    if (!countryCode || !languageCode || !secondaryLanguageCode) {
      return;
    }
    const primaryLanguage = this.getLanguageDetails(countryCode, languageCode);
    const secondaryLanguage = this.getLanguageDetails(
      countryCode,
      secondaryLanguageCode
    );
    const primaryContent = this.getTranslatedContent(
      primaryLanguage?.languageCode
    );
    const secondaryContent = this.getTranslatedContent(
      secondaryLanguage?.languageCode
    );
    this.setFlyOutConfirmationContent(
      countryCode,
      primaryContent,
      secondaryContent,
      primaryLanguage,
      secondaryLanguage,
      flyoutLangSelectorLink
    );
  }

  private getLanguageDetails(
    countryCode: string,
    languageCode: string
  ): LanguageDetails | undefined {
    return this.countryLanguageHelper.getLangaugeForLanguageCode(
      countryCode,
      languageCode
    );
  }

  private getTranslatedContent(
    languageCode: string | undefined
  ): string | null {
    return this.countryLanguageHelper.getTranslatedTextForLanguageCode(
      languageCode,
      'welcome-delta'
    );
  }

  setFlyOutConfirmationContent(
    countryCode: string,
    primaryContent: string | null,
    secondaryContent: string | null,
    primaryLanguage?: LanguageDetails,
    secondaryLanguage?: LanguageDetails,
    flyoutLangSelectorLink?: string
  ): void {
    if (primaryContent && secondaryContent) {
      this.flyOutConfirmationContent$ = of({
        primaryContent,
        secondaryContent,
        primaryLanguage,
        secondaryLanguage,
        countryCode,
        flyoutSelectorLinkLbl: flyoutLangSelectorLink || ''
      });
    }
  }
}
